import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ email_notifications_enabled: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData || JSON.parse(userData).type !== "admin") {
      navigate("/admin");
      return;
    }
    loadSettings();
  }, [navigate]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading settings:", error);
      setLoading(false);
    }
  };

  const handleToggleNotifications = async (enabled) => {
    try {
      await axios.put(`${API}/settings`, {
        email_notifications_enabled: enabled
      });
      setSettings({ ...settings, email_notifications_enabled: enabled });
      toast.success("Einstellungen gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <p className="text-lg">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/dashboard")}
            className="rounded-full"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-4xl font-bold text-gray-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Einstellungen
          </h1>
        </div>

        <div className="space-y-6">
          <Card className="shadow-xl rounded-3xl border-0">
            <CardHeader>
              <CardTitle>E-Mail Benachrichtigungen</CardTitle>
              <CardDescription>
                Senden Sie wöchentlich automatisch eine E-Mail an Mitarbeiter mit ihren Arbeitszeiten
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <Label htmlFor="email-notifications" className="text-base">
                  Wöchentliche E-Mail Benachrichtigungen
                </Label>
                <Switch
                  id="email-notifications"
                  checked={settings.email_notifications_enabled}
                  onCheckedChange={handleToggleNotifications}
                  data-testid="email-notifications-toggle"
                />
              </div>
              {settings.email_notifications_enabled && (
                <div className="mt-4 p-4 bg-blue-50 rounded-2xl">
                  <p className="text-sm text-blue-800">
                    <strong>Hinweis:</strong> Die E-Mail-Funktion ist derzeit als Mock implementiert. 
                    Sie können später einen E-Mail-Dienst wie SendGrid oder Mailgun konfigurieren, 
                    um tatsächlich E-Mails zu versenden.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-xl rounded-3xl border-0">
            <CardHeader>
              <CardTitle>Über diese App</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="font-semibold">Zeiterfassungs-App</p>
                <p className="text-sm text-gray-600">Version 1.0.0</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  Diese App ermöglicht eine einfache und moderne Arbeitszeiterfassung für Ihr Team.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
